import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom';

const STACK_ROOT_ID = 'livis-floating-stack-root';

// Single shared fixed-position flex container all "ambient" floating progress
// widgets (upload, augmentation, synthetic generation, ...) portal into, so the
// browser's own flex-wrap handles stacking them on top of one another when there's
// vertical room, or side by side once it runs out — instead of every widget
// hard-coding a fixed bottom/right offset independently.
const getFloatingStackRoot = (): HTMLElement => {
  let root = document.getElementById(STACK_ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = STACK_ROOT_ID;
    root.className = 'livis-floating-stack-root';
    document.body.appendChild(root);
  }
  return root;
};

interface FloatingStackPortalProps {
  // Lower order sits closer to the anchor corner (bottom-most / frontmost card
  // when collapsed into a peeking stack). Fixed priority in use across the app:
  // action-required prompts (FloatingPanel) = 0, upload = 1, augmentation = 2,
  // synthetic generation = 3. Must be a contiguous 0-based sequence — depth is
  // derived directly from it — so don't skip/duplicate values across widgets.
  order: number;
  children: ReactNode;
}

// Portals `children` into the shared floating-stack root. Use for any ambient
// floating widget that should stack/reflow alongside sibling progress widgets.
//
// Collapsed (not hovered): widgets overlap into a single peeking card stack —
// only the front card is fully visible, the rest peek out above it, purely via
// CSS (--stack-depth custom property + transform/margin), no JS position math.
// Hovered: the peek transform resets to 0 and the root's normal flex layout
// (column-reverse + wrap-reverse) takes over, spreading widgets out on top of
// one another or side by side depending on how much viewport room is left.
export const FloatingStackPortal: React.FC<FloatingStackPortalProps> = ({ order, children }) => {
  const depth = Math.max(0, order);
  return ReactDOM.createPortal(
    <div
      className="livis-floating-stack-item"
      style={{ order, ['--stack-depth' as any]: depth }}
    >
      {children}
    </div>,
    getFloatingStackRoot()
  );
};

export default FloatingStackPortal;